import type { EventBus } from './EventBus.js'
import type { CampaignMissionDef, CampaignObjective, CampaignSaveEntry, GameEvent } from '../interfaces/index.js'

const SAVE_KEY = 'ultimaze_campaign_progress'

export class CampaignSystem {
  private static _instance: CampaignSystem | null = null

  // Active tracking
  private missionId = ''
  private objective: CampaignObjective | null = null
  private progress = 0
  private complete = false
  private failed = false
  private completeCbs: Array<(success: boolean) => void> = []
  private unsubscribers: Array<() => void> = []
  private startTimeMs = 0

  static getInstance(): CampaignSystem {
    if (!CampaignSystem._instance) CampaignSystem._instance = new CampaignSystem()
    return CampaignSystem._instance
  }

  // ── Persistence ────────────────────────────────────

  getSavedProgress(): Record<string, CampaignSaveEntry> {
    try {
      const raw = localStorage.getItem(SAVE_KEY)
      return raw ? (JSON.parse(raw) as Record<string, CampaignSaveEntry>) : {}
    } catch {
      return {}
    }
  }

  isMissionCompleted(missionId: string): boolean {
    return this.getSavedProgress()[missionId]?.completed === true
  }

  saveResult(missionId: string, stars: number): void {
    const data = this.getSavedProgress()
    const existing = data[missionId]
    if (!existing || stars > existing.stars) {
      data[missionId] = { stars, completed: stars > 0 }
      try { localStorage.setItem(SAVE_KEY, JSON.stringify(data)) } catch { /* noop */ }
    }
  }

  // ── Active mission tracking ────────────────────────

  startMission(mission: CampaignMissionDef, bus: EventBus, localPlayerId: string): void {
    this.reset()
    this.missionId = mission.id
    this.objective = mission.objective
    this.startTimeMs = performance.now()

    const obj = mission.objective

    switch (obj.type) {
      case 'kill_units': {
        const unsub = bus.on('UNIT_KILLED', (e: GameEvent) => {
          // Bot units are owned by the bot player (not local player)
          if (e.playerId !== localPlayerId) {
            this.progress++
            this._checkThreshold()
          }
        })
        this.unsubscribers.push(unsub)
        break
      }

      case 'reach_income': {
        const unsub = bus.on('GOLD_CHANGED', (e: GameEvent) => {
          if (e.playerId === localPlayerId) {
            const income = (e.payload as { newIncome?: number }).newIncome ?? 0
            this.progress = income
            this._checkThreshold()
          }
        })
        this.unsubscribers.push(unsub)
        break
      }

      case 'destroy_base':
      case 'survive': {
        const unsub = bus.on('MATCH_END', (e: GameEvent) => {
          if (e.playerId !== localPlayerId) return
          const payload = e.payload as { outcome?: string }
          if (obj.type === 'destroy_base') {
            this.complete = payload.outcome === 'win'
            this.failed = payload.outcome !== 'win'
          } else {
            const elapsedSec = (performance.now() - this.startTimeMs) / 1000
            this.complete = elapsedSec >= obj.target || payload.outcome === 'win'
            this.failed = !this.complete
            this.progress = Math.min(elapsedSec, obj.target)
          }
          this.completeCbs.forEach((cb) => cb(this.complete))
        })
        this.unsubscribers.push(unsub)
        break
      }
    }
  }

  private _checkThreshold(): void {
    if (!this.objective || this.complete) return
    if (this.progress >= this.objective.target) {
      this.complete = true
      this.completeCbs.forEach((cb) => cb(true))
    }
  }

  /** 0.0 – 1.0 progress toward the objective target. */
  getProgress(): number {
    if (!this.objective || this.objective.target === 0) return 0
    return Math.min(this.progress / this.objective.target, 1)
  }

  isComplete(): boolean { return this.complete }
  isFailed(): boolean { return this.failed }
  getMissionId(): string { return this.missionId }

  onComplete(cb: (success: boolean) => void): void {
    this.completeCbs.push(cb)
  }

  reset(): void {
    this.unsubscribers.forEach((fn) => fn())
    this.unsubscribers = []
    this.completeCbs = []
    this.missionId = ''
    this.objective = null
    this.progress = 0
    this.complete = false
    this.failed = false
    this.startTimeMs = 0
  }
}
